import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Spinner from '../common/spinner';
import ConnectedTree from './../treeview/connectedtree';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import Footer from '../main/footer';
import FooterPadding from '../main/footerpadding';
import NotesView, { TypeCustomer } from './notesview';
import CustomerViewBasicInfo from './customerviewbasicinfo';
import {
    completeLeadTask,
    captureLeadPortfolio,
    getCustomerFull,
    getLeadCommunications,
    getLeadStageHistory,
    getLeadTasks,
    saveCustomer,
    saveLeadCommunication,
    saveLeadTask,
} from '../modules/customerdata';
import { saveLocation } from '../modules/locationdata';
import LocationNameInputDialog from '../treeview/locationNameInputDialog';
import Location from '../domain/location';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Accordion from '@mui/material/Accordion';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PartyView from '../common/partyview';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { addOrdersFromExcel } from '../modules/orderdata';
import CircularProgress from '@mui/material/CircularProgress';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import ParameterValue from '../domain/parametervalue';
import CustomershipStatus from '../domain/customershipstatus';
import LeadStage, { buildLeadStageOptions } from '../domain/leadstage';
import { buildTaskTypeOptions } from '../domain/tasktype';
import LeadLostReason from '../domain/leadlostreason';
import CommunicationDirection, { getCommunicationDirectionTranslationKey } from '../domain/communicationdirection';
import Customer from '../domain/customer';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CallMadeIcon from '@mui/icons-material/CallMade';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { toast } from 'react-toastify';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';

// Extracted as its own component (rather than inlined JSX in CustomerView) so the three
// conditional chips/text pieces in the collapsed Follow-up panel header get their own small
// function scope for static analysis, keeping them isolated from the already-large CustomerView
// component body.
function LeadFollowUpSummary({ t, openTaskCount, nextDueTask, latestCommunication }) {
    return (
        <Grid container alignItems="center" spacing={1}>
            <Grid item xs={12} md="auto">
                <h5 style={{ margin: 0 }}>{t('customer.leadFollowUpTitle')}</h5>
            </Grid>
            <Grid item xs="auto">
                <Chip
                    size="small"
                    color={openTaskCount > 0 ? 'primary' : 'default'}
                    label={`${t('customer.leadFollowUpOpenTasks')}: ${openTaskCount}`}
                />
            </Grid>
            {nextDueTask && (
                <Grid item xs="auto">
                    <Chip
                        size="small"
                        color={nextDueTask.dueAt.isBefore(moment()) ? 'error' : 'default'}
                        variant="outlined"
                        label={`${t('customer.leadFollowUpNextDueTask')}: ${nextDueTask.dueAt.format('DD.MM.YYYY HH:mm')}`}
                    />
                </Grid>
            )}
            {latestCommunication && (
                <Grid item xs>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
                        {t('customer.leadFollowUpLatestCommunication')}:{' '}
                        {moment(latestCommunication.occurredAt).format('DD.MM.YYYY HH:mm')} - {latestCommunication.description}
                    </Typography>
                </Grid>
            )}
            {!nextDueTask && !latestCommunication && (
                <Grid item xs>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {t('customer.leadFollowUpNoActivity')}
                    </Typography>
                </Grid>
            )}
        </Grid>
    );
}

export default function CustomerView(props) {
    const dispatch = useDispatch();
    const customerId = parseInt(props.match.params.customerId, 10);
    const [editTree, setEditTree] = useState(false);
    const [locationNameInputDialogOpen, setLocationNameInputDialogOpen] = useState(false);
    const customersById = useSelector((state) => state.customerdata.customersById);
    const getCustomerByIdInProgress = useSelector((state) => state.customerdata.getCustomerByIdInProgress);
    const getCustomerByIdFailed = useSelector((state) => state.customerdata.getCustomerByIdFailed);
    const userProfile = useSelector((state) => state.authentication.userProfile);
    const configurableLeadStages = useSelector((state) =>
        state.parameterdata && state.parameterdata.parameters ? state.parameterdata.parameters.LeadStage : null
    );
    const configurableTaskTypes = useSelector((state) =>
        state.parameterdata && state.parameterdata.parameters ? state.parameterdata.parameters.TaskType : null
    );
    const showcaseMode = useSelector((state) => state.appstate.showcaseMode);
    const leadStageHistoryByCustomerId =
        useSelector((state) => (state.customerdata ? state.customerdata.leadStageHistoryByCustomerId : null)) || {};
    const leadCommunicationsByCustomerId =
        useSelector((state) => (state.customerdata ? state.customerdata.leadCommunicationsByCustomerId : null)) || {};
    const [uploading, setUploading] = useState(false);
    const [savingLeadStage, setSavingLeadStage] = useState(false);
    const [closeoutSavedSuccess, setCloseoutSavedSuccess] = useState(false);
    const [leadDetailsExpanded, setLeadDetailsExpanded] = useState(true);
    const [followUpExpanded, setFollowUpExpanded] = useState(false);
    const [leadHistoryExpanded, setLeadHistoryExpanded] = useState(false);
    const [newLeadTaskDesc, setNewLeadTaskDesc] = useState('');
    const [newLeadTaskDueAt, setNewLeadTaskDueAt] = useState('');
    const [newLeadTaskType, setNewLeadTaskType] = useState(1);
    const [savingLeadTask, setSavingLeadTask] = useState(false);
    const [newLeadCommunicationDesc, setNewLeadCommunicationDesc] = useState('');
    const [newLeadCommunicationAt, setNewLeadCommunicationAt] = useState('');
    const [newLeadCommunicationDirection, setNewLeadCommunicationDirection] = useState(CommunicationDirection.Outbound());
    const [savingLeadCommunication, setSavingLeadCommunication] = useState(false);
    const [showAllCommunications, setShowAllCommunications] = useState(false);
    const [showAllTimelineEvents, setShowAllTimelineEvents] = useState(false);
    const [leadWonAtInput, setLeadWonAtInput] = useState('');
    const [leadWonValueInput, setLeadWonValueInput] = useState('');
    const [leadLostReasonCodeInput, setLeadLostReasonCodeInput] = useState('');
    const [leadLostReasonTextInput, setLeadLostReasonTextInput] = useState('');
    const [leadPortfolioDialogOpen, setLeadPortfolioDialogOpen] = useState(false);
    const [leadPortfolioSelection, setLeadPortfolioSelection] = useState('');
    const [capturingLeadPortfolio, setCapturingLeadPortfolio] = useState(false);
    const { t } = useTranslation();
    const contracts = useSelector((state) => state.contractdata.contractsById);
    const getCustomerFullFromProps = (id) => {
        return customersById[id];
    };
    let customerFull = getCustomerFullFromProps(customerId);
    const leadCaptureAvailable =
        customerFull &&
        (customerFull.status === CustomershipStatus.Potential() || customerFull.status === CustomershipStatus.Lead());
    const leadSectionEnabled = customerFull && customerFull.status === CustomershipStatus.Lead();
    // A Won lead becomes a regular Active customer (see CustomerDataMapper.UpdateOrInsert), but its
    // leadStage is left as Won rather than cleared - use that to tell "was a lead, now active" apart
    // from a customer that was always Active and never went through the pipeline at all.
    const wonLeadFollowUpEnabled =
        customerFull && customerFull.status === CustomershipStatus.Active() && customerFull.leadStage === LeadStage.Won();
    const leadIsClosed = customerFull && (customerFull.leadStage === LeadStage.Won() || customerFull.leadStage === LeadStage.Lost());
    const isLostAndSaved =
        customerFull &&
        customerFull.leadStage === LeadStage.Lost() &&
        (customerFull.leadLostReasonCode != null ||
            (customerFull.leadLostReasonText && customerFull.leadLostReasonText.trim() !== '') ||
            closeoutSavedSuccess);
    const leadStageHistory = leadStageHistoryByCustomerId[customerId] || [];
    const leadCommunications = leadCommunicationsByCustomerId[customerId] || [];
    const leadPortfolioCaptured = customerFull && customerFull.status === CustomershipStatus.Lead();

    useEffect(() => {
        (async () => {
            if (customersById[customerId] == null && !getCustomerByIdInProgress) {
                getCustomerFull(customerId)(dispatch);
            }
        })();
    }, [customerId, customersById, dispatch, getCustomerByIdInProgress]);

    useEffect(() => {
        if (leadSectionEnabled || wonLeadFollowUpEnabled) {
            getLeadTasks(customerId)(dispatch);
            getLeadStageHistory(customerId)(dispatch);
            getLeadCommunications(customerId)(dispatch);
        }
    }, [customerId, dispatch, leadSectionEnabled, wonLeadFollowUpEnabled]);

    useEffect(() => {
        if (customerFull) {
            // Keep the lead details expanded for the whole time the customer is in the Lead
            // section (including once stage is Won/Lost) so the closeout card stays visible until
            // the user actually saves it - it used to auto-collapse right when picking Won/Lost,
            // hiding the required next step.
            setLeadDetailsExpanded(true);
            setLeadWonAtInput(
                customerFull.leadWonAt ? moment.utc(customerFull.leadWonAt).local().format('YYYY-MM-DDTHH:mm') : ''
            );
            setLeadWonValueInput(customerFull.leadWonValue != null ? customerFull.leadWonValue : '');
            setLeadLostReasonCodeInput(customerFull.leadLostReasonCode != null ? customerFull.leadLostReasonCode : '');
            setLeadLostReasonTextInput(customerFull.leadLostReasonText || '');
        }
    }, [customerFull, leadSectionEnabled]);

    const onEditItem = () => {
        props.history.push('/customers/edit/' + customerId);
    };

    const onClose = () => {
        props.history.goBack();
    };

    const onActivateEdit = () => {
        setEditTree(true);
    };
    const onEditComplete = () => {
        setEditTree(false);
    };

    const onOpenLeadPortfolioDialog = () => {
        setLeadPortfolioSelection(
            leadPortfolioCaptured ? customerFull.portfolio.id : userProfile.selectedPortfolioId || ''
        );
        setLeadPortfolioDialogOpen(true);
    };

    const onCloseLeadPortfolioDialog = () => {
        setLeadPortfolioDialogOpen(false);
    };

    const onConfirmLeadPortfolio = () => {
        if (!leadPortfolioSelection) return;
        setCapturingLeadPortfolio(true);
        captureLeadPortfolio(customerId, leadPortfolioSelection)(dispatch)
            .then(() => {
                setCapturingLeadPortfolio(false);
                setLeadPortfolioDialogOpen(false);
                toast.info(t('toast.savedSuccess'), { autoClose: 2000, hideProgressBar: true });
            })
            .catch(() => {
                setCapturingLeadPortfolio(false);
            });
    };

    const showLocationInputNameDialog = (show) => {
        setLocationNameInputDialogOpen(show);
    };
    const saveNewLocation = (name) => {
        Location.NewObject().then((loc) => {
            loc.name = name;
            loc.customer = getCustomerFullFromProps(customerId);
            if (customerFull.electronicInvoiceAddress != null) {
                loc.useCustomerEInvoiceInfo = true;
            }
            if (customerFull.billingAddress != null) {
                loc.useCustomerBillingAddress = true;
            }
            if (customerFull.contractNumber != null) {
                loc.contractNumber = customerFull.contractNumber;
            } else {
                loc.contractNumber = new ParameterValue({
                    category: 'ContractNumber',
                    id: 1,
                    value: 'Ei sopimustunnusta',
                });
            }
            // save location
            dispatch(saveLocation(loc, true, false)).then(() => {
                showLocationInputNameDialog(false);
                props.history.push('/locations/' + loc.id);
                props.history.push('/locations/edit/' + loc.id);
            });
        });
    };

    const usedInLocationsDom = (title, locations) => {
        return (
            <Accordion sx={{ marginTop: '10px' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
                    <h5>
                        {title} ({locations.length} kpl)
                    </h5>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container>
                        {locations.length === 0 && <Grid item>{t('customer.noLocations')}</Grid>}
                        {locations.length > 0 &&
                            locations.map((loc) => (
                                <Grid
                                    item
                                    key={loc.id}
                                    xs={12}
                                    onClick={() => props.history.push('/locations/' + loc.id)}>
                                    {loc.name}
                                </Grid>
                            ))}
                    </Grid>
                </AccordionDetails>
            </Accordion>
        );
    };

    const fileUpload = async (evt) => {
        const fileReader = new FileReader();
        setUploading(true);
        fileReader.readAsDataURL(evt.target.files[0]);
        fileReader.onload = async (e) => {
            try {
                await addOrdersFromExcel(customerId, fileReader.result)(dispatch);
                setUploading(false);
            } catch (err) {
                console.log(err);
                setUploading(false);
            }
        };
    };

    const leadStages = buildLeadStageOptions(configurableLeadStages, t);
    const taskTypes = buildTaskTypeOptions(configurableTaskTypes, t);

    useEffect(() => {
        if (taskTypes.length > 0 && !taskTypes.some((opt) => opt.value === newLeadTaskType)) {
            setNewLeadTaskType(taskTypes[0].value);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [configurableTaskTypes]);

    const getTaskTypeLabel = (taskType) => {
        const type = taskTypes.find((item) => item.value === taskType);
        return type ? type.label : t('customer.leadTaskTypeCall');
    };

    const leadLostReasons = [
        { value: LeadLostReason.Price(), label: t('customer.leadLostReasonPrice') },
        { value: LeadLostReason.Timing(), label: t('customer.leadLostReasonTiming') },
        { value: LeadLostReason.Competitor(), label: t('customer.leadLostReasonCompetitor') },
        { value: LeadLostReason.NoFit(), label: t('customer.leadLostReasonNoFit') },
        { value: LeadLostReason.NoResponse(), label: t('customer.leadLostReasonNoResponse') },
        { value: LeadLostReason.Other(), label: t('customer.leadLostReasonOther') },
    ];

    const getLeadStageLabel = (stageValue) => {
        const stage = leadStages.find((item) => item.value === stageValue);
        return stage ? stage.label : '-';
    };

    const timelineEvents = useMemo(() => {
        const events = [];
        leadStageHistory.forEach((item) => {
            events.push({
                key: 'stage-' + item.id,
                at: moment(item.changedAt),
                icon: 'stage',
                label: `${getLeadStageLabel(item.fromStage)} \u2192 ${getLeadStageLabel(item.toStage)}`,
            });
        });
        leadCommunications.forEach((item) => {
            events.push({
                key: 'comm-' + item.storageId,
                at: moment(item.occurredAt),
                icon: item.direction === CommunicationDirection.Inbound() ? 'inbound' : 'outbound',
                label: item.description,
            });
        });
        ((customerFull && customerFull.salesTasks) || []).forEach((task) => {
            if (task.completedAt) {
                events.push({
                    key: 'task-' + task.storageId,
                    at: task.completedAt,
                    icon: 'task',
                    label: task.description,
                });
            }
        });
        return events.sort((a, b) => b.at.valueOf() - a.at.valueOf());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leadStageHistory, leadCommunications, customerFull && customerFull.salesTasks]);

    // Compact summary shown in the collapsed Follow-up panel header for Won/Active customers -
    // salesTasks is already server-sorted with open tasks first (ordered by due_at ASC), and
    // leadCommunications is already server-sorted newest first, so no extra sorting is needed here.
    const openLeadTasks = ((customerFull && customerFull.salesTasks) || []).filter((task) => !task.completedAt);
    const nextDueLeadTask = openLeadTasks.length > 0 ? openLeadTasks[0] : null;
    const latestLeadCommunication = leadCommunications.length > 0 ? leadCommunications[0] : null;

    const getTimelineIcon = (icon) => {
        switch (icon) {
            case 'stage':
                return <SwapHorizIcon color="action" />;
            case 'inbound':
                return <CallReceivedIcon color="info" />;
            case 'task':
                return <TaskAltIcon color="success" />;
            default:
                return <CallMadeIcon color="action" />;
        }
    };

    const getLeadLostReasonLabel = (reasonValue) => {
        const reason = leadLostReasons.find((item) => item.value === reasonValue);
        return reason ? reason.label : '-';
    };

    const changeLeadStage = (leadStageValue) => {
        const updated = new Customer(customerFull);
        updated.leadStage = leadStageValue;
        if (leadStageValue !== LeadStage.Lost()) {
            updated.leadLostReasonCode = null;
            updated.leadLostReasonText = null;
        }
        if (leadStageValue !== LeadStage.Won()) {
            updated.leadWonAt = null;
            updated.leadWonValue = null;
        }
        setSavingLeadStage(true);
        saveCustomer(updated)(dispatch)
            .then(() => {
                getLeadStageHistory(customerId)(dispatch);
                setSavingLeadStage(false);
                setCloseoutSavedSuccess(false);
            })
            .catch(() => setSavingLeadStage(false));
    };

    const reopenLead = () => {
        const firstStageId =
            configurableLeadStages && configurableLeadStages.length > 0
                ? configurableLeadStages[0].id
                : LeadStage.New();
        const updated = new Customer(customerFull);
        updated.leadStage = firstStageId;
        updated.leadLostReasonCode = null;
        updated.leadLostReasonText = null;
        updated.leadWonAt = null;
        updated.leadWonValue = null;
        setSavingLeadStage(true);
        saveCustomer(updated)(dispatch)
            .then(() => {
                getLeadStageHistory(customerId)(dispatch);
                setSavingLeadStage(false);
                setCloseoutSavedSuccess(false);
            })
            .catch(() => setSavingLeadStage(false));
    };

    const addLeadTask = async () => {
        if (!newLeadTaskDesc || !newLeadTaskDueAt) return;
        setSavingLeadTask(true);
        await saveLeadTask({
            customerId: customerId,
            taskType: newLeadTaskType,
            description: newLeadTaskDesc,
            dueAt: moment(newLeadTaskDueAt).utc().toISOString(),
            assignedUserId: userProfile.id,
        })(dispatch);
        setSavingLeadTask(false);
        setNewLeadTaskDesc('');
        setNewLeadTaskDueAt('');
        setNewLeadTaskType(1);
    };

    const completeTask = async (task) => {
        if (task.completedAt) return;
        setSavingLeadTask(true);
        await completeLeadTask(task.storageId, customerId, null)(dispatch);
        setSavingLeadTask(false);
    };

    const addLeadCommunication = async () => {
        if (!newLeadCommunicationDesc || !newLeadCommunicationAt) return;
        setSavingLeadCommunication(true);
        await saveLeadCommunication({
            customerId: customerId,
            communicationType: 1,
            direction: newLeadCommunicationDirection,
            occurredAt: moment(newLeadCommunicationAt).utc().toISOString(),
            description: newLeadCommunicationDesc,
        })(dispatch);
        setSavingLeadCommunication(false);
        setNewLeadCommunicationDesc('');
        setNewLeadCommunicationAt('');
        setNewLeadCommunicationDirection(CommunicationDirection.Outbound());
    };

    const saveLeadCloseout = async () => {
        if (!leadIsClosed) return;
        const updated = new Customer(customerFull);
        if (customerFull.leadStage === LeadStage.Won()) {
            updated.status = CustomershipStatus.Active();
        }
        updated.leadWonAt = customerFull.leadStage === LeadStage.Won() && leadWonAtInput ? moment(leadWonAtInput).utc().toISOString() : null;
        updated.leadWonValue =
            customerFull.leadStage === LeadStage.Won() && leadWonValueInput !== ''
                ? parseFloat(leadWonValueInput)
                : null;
        updated.leadLostReasonCode =
            customerFull.leadStage === LeadStage.Lost() && leadLostReasonCodeInput !== ''
                ? parseInt(leadLostReasonCodeInput, 10)
                : null;
        updated.leadLostReasonText = customerFull.leadStage === LeadStage.Lost() ? leadLostReasonTextInput : '';
        setSavingLeadStage(true);
        saveCustomer(updated)(dispatch)
            .then(() => {
                getLeadStageHistory(customerId)(dispatch);
                setSavingLeadStage(false);
                setCloseoutSavedSuccess(true);
            })
            .catch(() => setSavingLeadStage(false));
    };

    if (customerFull == null) {
        return (
            <div>
                {getCustomerByIdInProgress && <Spinner padding="20px" title={t('general.loading')} />}
                {!getCustomerByIdInProgress && getCustomerByIdFailed && (
                    <div style={{ padding: '20px' }}>
                        <Alert severity="error">{t('customer.failedToRetrieveCustomerDataRetry')}</Alert>
                    </div>
                )}
            </div>
        );
    }

    return (
        <Container maxWidth={'lg'}>
            <Grid item xs={12}>
                <h2>
                    <Button name="btn-close" onClick={onClose}>
                        <i className="fas fa-chevron-left fa-2x"></i>&nbsp;&nbsp;
                    </Button>
                    {customerFull.name}
                </h2>

                {!getCustomerByIdInProgress && customerFull != null && (
                    <div>
                        <CustomerViewBasicInfo 
                            customer={customerFull} 
                            showcaseMode={showcaseMode}
                            contracts={contracts}
                        />

                        <NotesView id={customerId} type={TypeCustomer} />

                        {leadSectionEnabled && (
                            <Accordion
                                expanded={leadDetailsExpanded}
                                onChange={() => setLeadDetailsExpanded((prev) => !prev)}
                                sx={{ mt: 2 }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Grid container alignItems="center">
                                        <Grid item xs>
                                            <h5 style={{ margin: 0 }}>{t('customer.leadSectionTitle')}</h5>
                                        </Grid>
                                        <Grid item>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {t('customer.leadLatestState')}: {getLeadStageLabel(customerFull.leadStage || LeadStage.New())}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="h6" sx={{ mb: 1 }}>
                                                        {t('customer.leadTimelineTitle')}
                                                    </Typography>
                                                    {timelineEvents.length > 0 ? (
                                                        <>
                                                            <List sx={{ py: 0 }}>
                                                                {(showAllTimelineEvents ? timelineEvents : timelineEvents.slice(0, 5)).map((event) => (
                                                                    <ListItem key={event.key} sx={{ px: 0 }}>
                                                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                                                            {getTimelineIcon(event.icon)}
                                                                        </ListItemIcon>
                                                                        <ListItemText
                                                                            primary={event.label}
                                                                            secondary={event.at.format('DD.MM.YYYY HH:mm')}
                                                                        />
                                                                    </ListItem>
                                                                ))}
                                                            </List>
                                                            {timelineEvents.length > 5 && (
                                                                <Button
                                                                    size="small"
                                                                    sx={{ mt: 1 }}
                                                                    onClick={() => setShowAllTimelineEvents(!showAllTimelineEvents)}>
                                                                    {showAllTimelineEvents
                                                                        ? t('customer.showLess')
                                                                        : `${t('customer.showAll')} (${timelineEvents.length})`}
                                                                </Button>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <em>{t('customer.leadTimelineNoEvents')}</em>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={12} md={5}>
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="h6" sx={{ mb: 1 }}>
                                                        {t('customer.leadPipelineTitle')}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                                        <strong>{t('customer.leadCurrentStage')}:</strong>{' '}
                                                        {getLeadStageLabel(customerFull.leadStage || LeadStage.New())}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                                        <strong>{t('customer.leadLatestState')}:</strong>{' '}
                                                        {leadStageHistory.length > 0
                                                            ? getLeadStageLabel(leadStageHistory[0].toStage)
                                                            : getLeadStageLabel(customerFull.leadStage || LeadStage.New())}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                                        <strong>{t('customer.leadWonAt')}:</strong>{' '}
                                                        {customerFull.leadWonAt
                                                            ? moment(customerFull.leadWonAt).format('DD.MM.YYYY HH:mm')
                                                            : '-'}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                                        <strong>{t('customer.leadWonValue')}:</strong>{' '}
                                                        {customerFull.leadWonValue != null
                                                            ? new Intl.NumberFormat('fi-FI', {
                                                                  style: 'currency',
                                                                  currency: 'EUR',
                                                              }).format(customerFull.leadWonValue)
                                                            : '-'}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                                        <strong>{t('customer.leadSource')}:</strong>{' '}
                                                        {customerFull.leadSource || '-'}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                                        <strong>{t('customer.estimatedDealValue')}:</strong>{' '}
                                                        {customerFull.estimatedDealValue != null
                                                            ? new Intl.NumberFormat('fi-FI', {
                                                                  style: 'currency',
                                                                  currency: 'EUR',
                                                              }).format(customerFull.estimatedDealValue)
                                                            : '-'}
                                                    </Typography>
                                                    {customerFull.leadStage === LeadStage.Lost() && (
                                                        <Typography variant="body2">
                                                            <strong>{t('customer.leadLostReason')}:</strong>{' '}
                                                            {getLeadLostReasonLabel(customerFull.leadLostReasonCode)}
                                                            {customerFull.leadLostReasonText
                                                                ? ` • ${customerFull.leadLostReasonText}`
                                                                : ''}
                                                        </Typography>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={12} md={7}>
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="h6" sx={{ mb: 1 }}>
                                                        {t('customer.leadStageHistory')}
                                                    </Typography>
                                                    {leadStageHistory.length > 0 ? (
                                                        <List sx={{ py: 0 }}>
                                                            {leadStageHistory.map((item) => (
                                                                <ListItem key={item.id} sx={{ px: 0, alignItems: 'flex-start' }}>
                                                                    <ListItemText
                                                                        primary={`${moment(item.changedAt).format(
                                                                            'DD.MM.YYYY HH:mm'
                                                                        )} • ${getLeadStageLabel(
                                                                            item.fromStage
                                                                        )} → ${getLeadStageLabel(item.toStage)}`}
                                                                    />
                                                                </ListItem>
                                                            ))}
                                                        </List>
                                                    ) : (
                                                        <em>{t('customer.leadStageNoHistory')}</em>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <FormControl sx={{ mt: 1, width: '100%' }}>
                                                <InputLabel id="lead-stage-label">{t('customer.leadStage')}</InputLabel>
                                                <Select
                                                    labelId="lead-stage-label"
                                                    id="lead-stage"
                                                    name="lead-stage"
                                                    value={customerFull.leadStage || LeadStage.New()}
                                                    label={t('customer.leadStage')}
                                                    disabled={savingLeadStage}
                                                    onChange={(evt) => changeLeadStage(evt.target.value)}>
                                                    {leadStages.map((stage) => (
                                                        <MenuItem key={stage.value} value={stage.value}>
                                                            {stage.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            {leadIsClosed && (
                                                <Card variant="outlined" sx={{ mt: 1 }}>
                                                    <CardContent>
                                                        <Typography variant="h6" sx={{ mb: 1 }}>
                                                            {t('customer.leadCloseoutTitle')}
                                                        </Typography>
                                                        {customerFull.leadStage === LeadStage.Lost() && isLostAndSaved ? (
                                                            <Box sx={{ mt: 1 }}>
                                                                <Alert severity="success" sx={{ mb: 2 }}>
                                                                    {t('customer.leadLostSavedSuccess')}
                                                                </Alert>
                                                                <Typography variant="body2" sx={{ mb: 1 }}>
                                                                    <strong>{t('customer.leadLostReason')}:</strong>{' '}
                                                                    {getLeadLostReasonLabel(customerFull.leadLostReasonCode)}
                                                                </Typography>
                                                                {customerFull.leadLostReasonText && (
                                                                    <Typography variant="body2" sx={{ mb: 2 }}>
                                                                        <strong>{t('customer.leadLostReasonDetails')}:</strong>{' '}
                                                                        {customerFull.leadLostReasonText}
                                                                    </Typography>
                                                                )}
                                                                <Button
                                                                    variant="contained"
                                                                    color="primary"
                                                                    sx={{ mt: 1 }}
                                                                    disabled={savingLeadStage}
                                                                    onClick={reopenLead}>
                                                                    {t('customer.reopenLead')}
                                                                </Button>
                                                            </Box>
                                                        ) : (
                                                            <>
                                                                <Alert severity="info" sx={{ mb: 2 }}>
                                                                    {t(
                                                                        customerFull.leadStage === LeadStage.Won()
                                                                            ? 'customer.leadCloseoutInstructionsWon'
                                                                            : 'customer.leadCloseoutInstructionsLost'
                                                                    )}
                                                                </Alert>
                                                                {customerFull.leadStage === LeadStage.Won() && (
                                                                    <Grid container spacing={1}>
                                                                        <Grid item xs={12} md={6}>
                                                                            <TextField
                                                                                fullWidth
                                                                                size="small"
                                                                                type="datetime-local"
                                                                                label={t('customer.leadWonAt')}
                                                                                InputLabelProps={{ shrink: true }}
                                                                                value={leadWonAtInput}
                                                                                onChange={(evt) => setLeadWonAtInput(evt.target.value)}
                                                                            />
                                                                        </Grid>
                                                                        <Grid item xs={12} md={6}>
                                                                            <TextField
                                                                                fullWidth
                                                                                size="small"
                                                                                type="number"
                                                                                inputProps={{ step: '0.01' }}
                                                                                label={t('customer.leadWonValue')}
                                                                                value={leadWonValueInput}
                                                                                onChange={(evt) => setLeadWonValueInput(evt.target.value)}
                                                                            />
                                                                        </Grid>
                                                                    </Grid>
                                                                )}
                                                                {customerFull.leadStage === LeadStage.Lost() && (
                                                                    <Grid container spacing={1}>
                                                                        <Grid item xs={12} md={6}>
                                                                            <FormControl sx={{ width: '100%' }} size="small">
                                                                                <InputLabel id="lead-lost-reason-label">
                                                                                    {t('customer.leadLostReason')}
                                                                                </InputLabel>
                                                                                <Select
                                                                                    labelId="lead-lost-reason-label"
                                                                                    value={leadLostReasonCodeInput}
                                                                                    label={t('customer.leadLostReason')}
                                                                                    onChange={(evt) =>
                                                                                        setLeadLostReasonCodeInput(evt.target.value)
                                                                                    }>
                                                                                    {leadLostReasons.map((reason) => (
                                                                                        <MenuItem key={reason.value} value={reason.value}>
                                                                                            {reason.label}
                                                                                        </MenuItem>
                                                                                    ))}
                                                                                </Select>
                                                                            </FormControl>
                                                                        </Grid>
                                                                        <Grid item xs={12} md={6}>
                                                                            <TextField
                                                                                fullWidth
                                                                                size="small"
                                                                                label={t('customer.leadLostReasonDetails')}
                                                                                value={leadLostReasonTextInput}
                                                                                onChange={(evt) =>
                                                                                    setLeadLostReasonTextInput(evt.target.value)
                                                                                }
                                                                            />
                                                                        </Grid>
                                                                    </Grid>
                                                                )}
                                                                <Button
                                                                    variant="contained"
                                                                    sx={{ mt: 2 }}
                                                                    disabled={savingLeadStage}
                                                                    onClick={saveLeadCloseout}>
                                                                    {t('buttons.save')}
                                                                </Button>
                                                            </>
                                                        )}
                                                    </CardContent>
                                                </Card>
                                            )}
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                    <Card variant="outlined" sx={{ mt: 1 }}>
                                        <CardContent>
                                            <Typography variant="h6" sx={{ mb: 1 }}>
                                                {t('customer.leadTasksTitle')}
                                            </Typography>
                                            <Grid container spacing={1}>
                                                <Grid item xs={12} md={3}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>{t('customer.leadTaskType')}</InputLabel>
                                                        <Select
                                                            value={newLeadTaskType}
                                                            label={t('customer.leadTaskType')}
                                                            onChange={(evt) => setNewLeadTaskType(evt.target.value)}>
                                                            {taskTypes.map((type) => (
                                                                <MenuItem key={type.value} value={type.value}>
                                                                    {type.label}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        label={t('customer.leadTaskDescription')}
                                                        value={newLeadTaskDesc}
                                                        onChange={(evt) => setNewLeadTaskDesc(evt.target.value)}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={3}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        type="datetime-local"
                                                        label={t('customer.leadTaskDueAt')}
                                                        InputLabelProps={{ shrink: true }}
                                                        value={newLeadTaskDueAt}
                                                        onChange={(evt) => setNewLeadTaskDueAt(evt.target.value)}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={2}>
                                                    <Button
                                                        fullWidth
                                                        variant="contained"
                                                        size="medium"
                                                        disabled={savingLeadTask || !newLeadTaskDesc || !newLeadTaskDueAt}
                                                        onClick={addLeadTask}>
                                                        {t('buttons.add')}
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                            {customerFull.salesTasks && customerFull.salesTasks.length > 0 && (
                                                <List>
                                                    {customerFull.salesTasks.map((task) => (
                                                        <ListItem key={task.storageId} sx={{ px: 0 }}>
                                                            <Checkbox
                                                                checked={task.completedAt != null}
                                                                disabled={savingLeadTask}
                                                                onChange={() => completeTask(task)}
                                                            />
                                                            <Grid container alignItems={'center'} spacing={1}>
                                                                <Grid item xs={12} md={6}>
                                                                    <span
                                                                        style={
                                                                            task.completedAt
                                                                                ? {
                                                                                      textDecoration: 'line-through',
                                                                                      color: '#666',
                                                                                  }
                                                                                : {}
                                                                        }>
                                                                        {task.description}
                                                                    </span>
                                                                </Grid>
                                                                <Grid item xs={6} md={3}>
                                                                    <Chip
                                                                        size="small"
                                                                        variant="outlined"
                                                                        label={getTaskTypeLabel(task.taskType)}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={6} md={3}>
                                                                    <Chip
                                                                        size="small"
                                                                        color={
                                                                            task.completedAt
                                                                                ? 'default'
                                                                                : task.dueAt.isBefore(moment())
                                                                                ? 'error'
                                                                                : 'primary'
                                                                        }
                                                                        label={
                                                                            task.completedAt
                                                                                ? t('customer.leadTaskCompleted')
                                                                                : t('customer.leadTaskDue') +
                                                                                  ': ' +
                                                                                  task.dueAt.format(
                                                                                      'DD.MM.YYYY HH:mm'
                                                                                  )
                                                                        }
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            )}
                                            {(!customerFull.salesTasks || customerFull.salesTasks.length === 0) && (
                                                <em>{t('customer.leadTaskNoTasks')}</em>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12}>
                                    <Card variant="outlined" sx={{ mt: 1 }}>
                                        <CardContent>
                                            <Typography variant="h6" sx={{ mb: 1 }}>
                                                {t('customer.leadCommunicationsTitle')}
                                            </Typography>
                                            <Grid container spacing={1}>
                                                <Grid item xs={12} md={5}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        label={t('customer.leadCommunicationDescription')}
                                                        value={newLeadCommunicationDesc}
                                                        onChange={(evt) => setNewLeadCommunicationDesc(evt.target.value)}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={3}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        type="datetime-local"
                                                        label={t('customer.leadCommunicationOccurredAt')}
                                                        InputLabelProps={{ shrink: true }}
                                                        value={newLeadCommunicationAt}
                                                        onChange={(evt) => setNewLeadCommunicationAt(evt.target.value)}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={2}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>{t('customer.leadCommunicationDirection')}</InputLabel>
                                                        <Select
                                                            label={t('customer.leadCommunicationDirection')}
                                                            value={newLeadCommunicationDirection}
                                                            onChange={(evt) => setNewLeadCommunicationDirection(evt.target.value)}>
                                                            <MenuItem value={CommunicationDirection.Outbound()}>
                                                                {t('customer.leadCommunicationDirectionOutbound')}
                                                            </MenuItem>
                                                            <MenuItem value={CommunicationDirection.Inbound()}>
                                                                {t('customer.leadCommunicationDirectionInbound')}
                                                            </MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12} md={2}>
                                                    <Button
                                                        fullWidth
                                                        variant="contained"
                                                        size="medium"
                                                        disabled={
                                                            savingLeadCommunication ||
                                                            !newLeadCommunicationDesc ||
                                                            !newLeadCommunicationAt
                                                        }
                                                        onClick={addLeadCommunication}>
                                                        {t('buttons.add')}
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                            {leadCommunications.length > 0 ? (
                                                <>
                                                    <List>
                                                        {(showAllCommunications ? leadCommunications : leadCommunications.slice(0, 5)).map((item) => (
                                                            <ListItem key={item.storageId} sx={{ px: 0 }}>
                                                                <ListItemText
                                                                    primary={
                                                                        <>
                                                                            <Chip
                                                                                size="small"
                                                                                label={t(
                                                                                    getCommunicationDirectionTranslationKey(
                                                                                        item.direction
                                                                                    ) || 'customer.leadCommunicationDirectionOutbound'
                                                                                )}
                                                                                color={
                                                                                    item.direction === CommunicationDirection.Inbound()
                                                                                        ? 'info'
                                                                                        : 'default'
                                                                                }
                                                                                sx={{ mr: 1 }}
                                                                            />
                                                                            {item.description}
                                                                        </>
                                                                    }
                                                                    secondary={moment(item.occurredAt).format(
                                                                        'DD.MM.YYYY HH:mm'
                                                                    )}
                                                                />
                                                            </ListItem>
                                                        ))}
                                                    </List>
                                                    {leadCommunications.length > 5 && (
                                                        <Button
                                                            size="small"
                                                            sx={{ mt: 1 }}
                                                            onClick={() => setShowAllCommunications(!showAllCommunications)}>
                                                            {showAllCommunications
                                                                ? t('customer.showLess')
                                                                : `${t('customer.showAll')} (${leadCommunications.length})`}
                                                        </Button>
                                                    )}
                                                </>
                                            ) : (
                                                <em>{t('customer.leadCommunicationNoItems')}</em>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>
                        )}

                        {wonLeadFollowUpEnabled && (
                            <>
                                {/* Won leads become regular Active customers - the day-to-day focus shifts to
                                    locations, so tasks/communications stay collapsed by default here (unlike the
                                    always-expanded pipeline accordion above for still-open leads), and the closed
                                    pipeline/stage details move into a separate, de-emphasised read-only section. */}
                                <Accordion
                                    expanded={followUpExpanded}
                                    onChange={() => setFollowUpExpanded((prev) => !prev)}
                                    sx={{ mt: 2 }}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <LeadFollowUpSummary
                                            t={t}
                                            openTaskCount={openLeadTasks.length}
                                            nextDueTask={nextDueLeadTask}
                                            latestCommunication={latestLeadCommunication}
                                        />
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <Card variant="outlined">
                                                    <CardContent>
                                                        <Typography variant="h6" sx={{ mb: 1 }}>
                                                            {t('customer.leadTasksTitle')}
                                                        </Typography>
                                                        <Grid container spacing={1}>
                                                            <Grid item xs={12} md={3}>
                                                                <FormControl fullWidth size="small">
                                                                    <InputLabel>{t('customer.leadTaskType')}</InputLabel>
                                                                    <Select
                                                                        value={newLeadTaskType}
                                                                        label={t('customer.leadTaskType')}
                                                                        onChange={(evt) => setNewLeadTaskType(evt.target.value)}>
                                                                        {taskTypes.map((type) => (
                                                                            <MenuItem key={type.value} value={type.value}>
                                                                                {type.label}
                                                                            </MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                </FormControl>
                                                            </Grid>
                                                            <Grid item xs={12} md={4}>
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    label={t('customer.leadTaskDescription')}
                                                                    value={newLeadTaskDesc}
                                                                    onChange={(evt) => setNewLeadTaskDesc(evt.target.value)}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12} md={3}>
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    type="datetime-local"
                                                                    label={t('customer.leadTaskDueAt')}
                                                                    InputLabelProps={{ shrink: true }}
                                                                    value={newLeadTaskDueAt}
                                                                    onChange={(evt) => setNewLeadTaskDueAt(evt.target.value)}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12} md={2}>
                                                                <Button
                                                                    fullWidth
                                                                    variant="contained"
                                                                    size="medium"
                                                                    disabled={savingLeadTask || !newLeadTaskDesc || !newLeadTaskDueAt}
                                                                    onClick={addLeadTask}>
                                                                    {t('buttons.add')}
                                                                </Button>
                                                            </Grid>
                                                        </Grid>
                                                        {customerFull.salesTasks && customerFull.salesTasks.length > 0 && (
                                                            <List>
                                                                {customerFull.salesTasks.map((task) => (
                                                                    <ListItem key={task.storageId} sx={{ px: 0 }}>
                                                                        <Checkbox
                                                                            checked={task.completedAt != null}
                                                                            disabled={savingLeadTask}
                                                                            onChange={() => completeTask(task)}
                                                                        />
                                                                        <Grid container alignItems={'center'} spacing={1}>
                                                                            <Grid item xs={12} md={6}>
                                                                                <span
                                                                                    style={
                                                                                        task.completedAt
                                                                                            ? { textDecoration: 'line-through', color: '#666' }
                                                                                            : {}
                                                                                    }>
                                                                                    {task.description}
                                                                                </span>
                                                                            </Grid>
                                                                            <Grid item xs={6} md={3}>
                                                                                <Chip
                                                                                    size="small"
                                                                                    variant="outlined"
                                                                                    label={getTaskTypeLabel(task.taskType)}
                                                                                />
                                                                            </Grid>
                                                                            <Grid item xs={6} md={3}>
                                                                                <Chip
                                                                                    size="small"
                                                                                    color={
                                                                                        task.completedAt
                                                                                            ? 'default'
                                                                                            : task.dueAt.isBefore(moment())
                                                                                            ? 'error'
                                                                                            : 'primary'
                                                                                    }
                                                                                    label={
                                                                                        task.completedAt
                                                                                            ? t('customer.leadTaskCompleted')
                                                                                            : t('customer.leadTaskDue') +
                                                                                              ': ' +
                                                                                              task.dueAt.format('DD.MM.YYYY HH:mm')
                                                                                    }
                                                                                />
                                                                            </Grid>
                                                                        </Grid>
                                                                    </ListItem>
                                                                ))}
                                                            </List>
                                                        )}
                                                        {(!customerFull.salesTasks || customerFull.salesTasks.length === 0) && (
                                                            <em>{t('customer.leadTaskNoTasks')}</em>
                                                        )}
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Card variant="outlined">
                                                    <CardContent>
                                                        <Typography variant="h6" sx={{ mb: 1 }}>
                                                            {t('customer.leadCommunicationsTitle')}
                                                        </Typography>
                                                        <Grid container spacing={1}>
                                                            <Grid item xs={12} md={5}>
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    label={t('customer.leadCommunicationDescription')}
                                                                    value={newLeadCommunicationDesc}
                                                                    onChange={(evt) => setNewLeadCommunicationDesc(evt.target.value)}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12} md={3}>
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    type="datetime-local"
                                                                    label={t('customer.leadCommunicationOccurredAt')}
                                                                    InputLabelProps={{ shrink: true }}
                                                                    value={newLeadCommunicationAt}
                                                                    onChange={(evt) => setNewLeadCommunicationAt(evt.target.value)}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12} md={2}>
                                                                <FormControl fullWidth size="small">
                                                                    <InputLabel>{t('customer.leadCommunicationDirection')}</InputLabel>
                                                                    <Select
                                                                        label={t('customer.leadCommunicationDirection')}
                                                                        value={newLeadCommunicationDirection}
                                                                        onChange={(evt) => setNewLeadCommunicationDirection(evt.target.value)}>
                                                                        <MenuItem value={CommunicationDirection.Outbound()}>
                                                                            {t('customer.leadCommunicationDirectionOutbound')}
                                                                        </MenuItem>
                                                                        <MenuItem value={CommunicationDirection.Inbound()}>
                                                                            {t('customer.leadCommunicationDirectionInbound')}
                                                                        </MenuItem>
                                                                    </Select>
                                                                </FormControl>
                                                            </Grid>
                                                            <Grid item xs={12} md={2}>
                                                                <Button
                                                                    fullWidth
                                                                    variant="contained"
                                                                    size="medium"
                                                                    disabled={
                                                                        savingLeadCommunication ||
                                                                        !newLeadCommunicationDesc ||
                                                                        !newLeadCommunicationAt
                                                                    }
                                                                    onClick={addLeadCommunication}>
                                                                    {t('buttons.add')}
                                                                </Button>
                                                            </Grid>
                                                        </Grid>
                                                        {leadCommunications.length > 0 ? (
                                                            <>
                                                                <List>
                                                                    {(showAllCommunications
                                                                        ? leadCommunications
                                                                        : leadCommunications.slice(0, 5)
                                                                    ).map((item) => (
                                                                        <ListItem key={item.storageId} sx={{ px: 0 }}>
                                                                            <ListItemText
                                                                                primary={
                                                                                    <>
                                                                                        <Chip
                                                                                            size="small"
                                                                                            label={t(
                                                                                                getCommunicationDirectionTranslationKey(
                                                                                                    item.direction
                                                                                                ) || 'customer.leadCommunicationDirectionOutbound'
                                                                                            )}
                                                                                            color={
                                                                                                item.direction === CommunicationDirection.Inbound()
                                                                                                    ? 'info'
                                                                                                    : 'default'
                                                                                            }
                                                                                            sx={{ mr: 1 }}
                                                                                        />
                                                                                        {item.description}
                                                                                    </>
                                                                                }
                                                                                secondary={moment(item.occurredAt).format('DD.MM.YYYY HH:mm')}
                                                                            />
                                                                        </ListItem>
                                                                    ))}
                                                                </List>
                                                                {leadCommunications.length > 5 && (
                                                                    <Button
                                                                        size="small"
                                                                        sx={{ mt: 1 }}
                                                                        onClick={() => setShowAllCommunications(!showAllCommunications)}>
                                                                        {showAllCommunications
                                                                            ? t('customer.showLess')
                                                                            : `${t('customer.showAll')} (${leadCommunications.length})`}
                                                                    </Button>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <em>{t('customer.leadCommunicationNoItems')}</em>
                                                        )}
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>

                                <Accordion
                                    expanded={leadHistoryExpanded}
                                    onChange={() => setLeadHistoryExpanded((prev) => !prev)}
                                    sx={{ mt: 1 }}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <h5 style={{ margin: 0 }}>{t('customer.leadHistoryTitle')}</h5>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <Card variant="outlined">
                                                    <CardContent>
                                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                                            <strong>{t('customer.leadWonAt')}:</strong>{' '}
                                                            {customerFull.leadWonAt
                                                                ? moment(customerFull.leadWonAt).format('DD.MM.YYYY HH:mm')
                                                                : '-'}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                                            <strong>{t('customer.leadWonValue')}:</strong>{' '}
                                                            {customerFull.leadWonValue != null
                                                                ? new Intl.NumberFormat('fi-FI', {
                                                                      style: 'currency',
                                                                      currency: 'EUR',
                                                                  }).format(customerFull.leadWonValue)
                                                                : '-'}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                                            <strong>{t('customer.leadSource')}:</strong>{' '}
                                                            {customerFull.leadSource || '-'}
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            <strong>{t('customer.estimatedDealValue')}:</strong>{' '}
                                                            {customerFull.estimatedDealValue != null
                                                                ? new Intl.NumberFormat('fi-FI', {
                                                                      style: 'currency',
                                                                      currency: 'EUR',
                                                                  }).format(customerFull.estimatedDealValue)
                                                                : '-'}
                                                        </Typography>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Card variant="outlined">
                                                    <CardContent>
                                                        <Typography variant="h6" sx={{ mb: 1 }}>
                                                            {t('customer.leadStageHistory')}
                                                        </Typography>
                                                        {leadStageHistory.length > 0 ? (
                                                            <List sx={{ py: 0 }}>
                                                                {leadStageHistory.map((item) => (
                                                                    <ListItem key={item.id} sx={{ px: 0, alignItems: 'flex-start' }}>
                                                                        <ListItemText
                                                                            primary={`${moment(item.changedAt).format(
                                                                                'DD.MM.YYYY HH:mm'
                                                                            )} \u2022 ${getLeadStageLabel(item.fromStage)} \u2192 ${getLeadStageLabel(
                                                                                item.toStage
                                                                            )}`}
                                                                        />
                                                                    </ListItem>
                                                                ))}
                                                            </List>
                                                        ) : (
                                                            <em>{t('customer.leadStageNoHistory')}</em>
                                                        )}
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>
                            </>
                        )}

                        {customerFull.partyRelations && (
                            <div style={{ marginBottom: '20px' }}>
                                <h5>{t('general.contactInformation')}</h5>
                                <PartyView showControls={false} parties={customerFull.partyRelations} />
                            </div>
                        )}

                        <Grid container>
                            <Grid item xs={12}>
                                <hr style={{ marginTop: '10px' }} />
                            </Grid>
                            <Grid item xs={12}>
                                <h5>{t('general.locations')}</h5>
                            </Grid>
                            <Grid container>
                                <Grid item md={2} xs={12}>
                                    {!editTree && (
                                        <Grid item>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                onClick={onActivateEdit}
                                                size={'small'}>
                                                <EditIcon />
                                                {t('buttons.edit')}
                                            </Button>
                                        </Grid>
                                    )}
                                    {editTree && (
                                        <Grid item>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                onClick={onEditComplete}
                                                size={'small'}>
                                                <CheckIcon />
                                                {t('buttons.close')}
                                            </Button>
                                        </Grid>
                                    )}
                                </Grid>
                                <Grid item md={10} xs={12}>
                                    <ConnectedTree
                                        edit={editTree}
                                        customer={customerFull}
                                        history={props.history}
                                        userPortfolios={userProfile.portfolioDescriptions}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        {customerFull != null && (
                            <Grid container>
                                <Grid item xs={12}>
                                    {usedInLocationsDom(
                                        t('customer.invoicingCustomerInLocations'),
                                        customerFull.customerAsBillingCustomerLocations
                                    )}
                                </Grid>
                                <Grid item xs={12}>
                                    {usedInLocationsDom(
                                        t('customer.deliveryCustomerInLocations'),
                                        customerFull.customerAsDeliveryCustomerLocations
                                    )}
                                </Grid>
                            </Grid>
                        )}
                    </div>
                )}
            </Grid>
            <Footer>
                <Grid item>
                    <Button
                        name="btn-muokkaa"
                        variant="contained"
                        color="primary"
                        onClick={onEditItem}
                        startIcon={<EditIcon />}>
                        {t('buttons.edit')}
                    </Button>
                </Grid>
                {leadCaptureAvailable && (
                    <Grid item>
                        <Button
                            name="btn-lead-portfolio"
                            variant="contained"
                            color="primary"
                            onClick={onOpenLeadPortfolioDialog}
                            startIcon={<MoveToInboxIcon />}>
                            {leadPortfolioCaptured ? t('buttons.moveLeadPortfolio') : t('buttons.captureLead')}
                        </Button>
                    </Grid>
                )}
                <Grid item>
                    <Button
                        name="btn-new-location"
                        variant="contained"
                        color="primary"
                        onClick={() => showLocationInputNameDialog(true)}
                        startIcon={<AddIcon />}>
                        {t('general.newLocation')}
                    </Button>
                </Grid>
                <Grid item>
                    <div>
                        <label htmlFor="upload-excel">
                            <input
                                style={{ display: 'none' }}
                                id="upload-excel"
                                name="upload-excel"
                                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                onChange={fileUpload}
                                type="file"
                            />

                            <Button
                                color="primary"
                                disabled={uploading}
                                component="span"
                                aria-label="add"
                                variant="contained"
                                startIcon={<AddShoppingCartIcon />}
                                endIcon={uploading ? <CircularProgress color={'secondary'} size={'1rem'} /> : ''}>
                                {t('buttons.addToCart')}&nbsp;(.xlsx)
                            </Button>
                        </label>
                    </div>
                </Grid>
                <Grid item>
                    <Button
                        name="btn-sulje"
                        variant="contained"
                        sx={{ bgcolor: 'grey.300', color: 'text.primary', '&:hover': { bgcolor: 'grey.400' } }}
                        onClick={onClose}
                        startIcon={<CloseIcon />}>
                        {t('buttons.close')}
                    </Button>
                </Grid>
            </Footer>
            <FooterPadding />
            <LocationNameInputDialog
                dialogOpen={locationNameInputDialogOpen}
                cancel={() => showLocationInputNameDialog(false)}
                confirm={saveNewLocation}
            />
            <Dialog open={leadPortfolioDialogOpen} onClose={onCloseLeadPortfolioDialog}>
                <DialogTitle>
                    {leadPortfolioCaptured ? t('buttons.moveLeadPortfolio') : t('buttons.captureLead')}
                </DialogTitle>
                <DialogContent>
                    <FormControl sx={{ mt: 1, minWidth: 300 }}>
                        <InputLabel id="lead-portfolio-select-label">{t('general.portfolio')}</InputLabel>
                        <Select
                            labelId="lead-portfolio-select-label"
                            id="lead-portfolio-select"
                            value={leadPortfolioSelection}
                            onChange={(evt) => setLeadPortfolioSelection(evt.target.value)}>
                            {(userProfile.portfolioDescriptions || []).map((p) => (
                                <MenuItem key={p.id} value={p.id}>
                                    {p.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onCloseLeadPortfolioDialog}>{t('buttons.cancel')}</Button>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={!leadPortfolioSelection || capturingLeadPortfolio}
                        onClick={onConfirmLeadPortfolio}>
                        {t('buttons.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}
